import { create } from "zustand";
import { ApiError, postJSON, apiFetch, type Account, type Membership, type User } from "../lib/api";

export type AuthStatus = "idle" | "loading" | "authenticated" | "anonymous";

interface AuthState {
  status: AuthStatus;
  user: User | null;
  memberships: Membership[];
  error: string | null;

  /** Resolves the current session from GET /auth/me (never throws). */
  fetchMe: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** POST /auth/forgot-password — always succeeds (no account enumeration). */
  forgotPassword: (email: string) => Promise<void>;
  clearError: () => void;
}

function applyAccount(state: AuthState, account: Account): AuthState {
  return {
    ...state,
    status: "authenticated",
    user: account.user,
    memberships: account.memberships,
    error: null,
  };
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return err.message;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return "Something went wrong";
}

export const useAuthStore = create<AuthState>((set) => ({
  status: "idle",
  user: null,
  memberships: [],
  error: null,

  fetchMe: async () => {
    set({ status: "loading", error: null });
    try {
      const account = await apiFetch<Account>("/auth/me");
      set((state) => applyAccount(state, account));
    } catch {
      // No session: the visitor is signed out (public pages still work).
      set({ status: "anonymous", user: null, memberships: [], error: null });
    }
  },

  login: async (email, password) => {
    set({ error: null });
    try {
      const account = await postJSON<Account>("/auth/login", {
        email,
        password,
      });
      set((state) => applyAccount(state, account));
    } catch (err) {
      set({ status: "anonymous", error: errorMessage(err) });
      throw err;
    }
  },

  signup: async (email, password) => {
    set({ error: null });
    try {
      const account = await postJSON<Account>("/auth/register", {
        email,
        password,
      });
      set((state) => applyAccount(state, account));
    } catch (err) {
      set({ status: "anonymous", error: errorMessage(err) });
      throw err;
    }
  },

  logout: async () => {
    set({ error: null });
    try {
      await postJSON<void>("/auth/logout", {});
    } catch {
      // Session may already be gone; clear locally either way.
    }
    set({ status: "anonymous", user: null, memberships: [] });
  },

  forgotPassword: async (email) => {
    set({ error: null });
    try {
      await postJSON<void>("/auth/forgot-password", { email });
    } catch (err) {
      set({ error: errorMessage(err) });
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}));
